// A new version reaches every screen without incognito (JL, 2026-11-01: "the latest version is not there").
//
// The app is kept on the device so it opens with no signal (vite-plugin-pwa). After a deploy the browser first shows the
// copy it kept, fetches the new one behind it, and the new one takes over only on a later reload; a tab left open for
// days never got it. Here the app looks for a new version when it opens, every 15 minutes, when the screen comes back
// and when the signal returns. Once the new version has arrived it reloads into it only at a moment nothing is lost:
// in the first seconds after opening, or on moving to another page (what the last page held is gone by then); never
// with work typed and not saved, something being saved, deliveries being sent, or no signal. Otherwise a band says a
// new version is ready, with Reload.
import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { confirmLeaving, nothingUnsaved } from './hooks';
import { useT } from './i18n';

const CHECK_EVERY_MS = 15 * 60_000;
/** Opened this recently and a new version arrives: nothing has been started yet, so it reloads at once. */
const JUST_OPENED_MS = 10_000;

let ready = false;
let reloading = false;
/** Something in flight that a reload would cut off (set by main.tsx: saves running, deliveries being sent). */
let busy: () => boolean = () => false;
const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

function reload() {
  if (reloading) return;
  reloading = true;
  // a reload the person called off (the browser's "Leave site?") can be asked again
  window.setTimeout(() => {
    reloading = false;
  }, 3000);
  window.location.reload();
}

/** Reload into the new version when it is ready and nothing would be lost: nothing typed unsaved, nothing in flight, signal. */
export function reloadIfReady(): boolean {
  if (!ready || !nothingUnsaved() || busy() || !navigator.onLine) return false;
  reload();
  return true;
}

/** For tests: the state as if a new version had (or had not) arrived. */
export function setUpdateReadyForTest(on: boolean) {
  ready = on;
  reloading = false;
  for (const fn of listeners) fn();
}

/** Register the app's service worker and keep it up to date (called once from main.tsx). */
export function setupAppUpdate(isBusy: () => boolean): void {
  busy = isBusy;
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  // a page opened with no version in charge (the first install, a hard reload) gets one now: that one is not new
  let hadVersion = navigator.serviceWorker.controller !== null;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadVersion) {
      hadVersion = true;
      return;
    }
    ready = true;
    for (const fn of listeners) fn();
    if (performance.now() < JUST_OPENED_MS) reloadIfReady();
  });
  navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then((reg) => {
      const check = () => void reg.update().catch(() => undefined);
      window.setInterval(check, CHECK_EVERY_MS);
      window.addEventListener('online', check);
      // coming back to the screen only looks: a page left half typed (a camera opened from the delivery form, another
      // window) is never reloaded under the person
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
    })
    .catch(() => undefined);
}

/** The band: a new version is ready (shown until it is reloaded into); moving to another page reloads by itself. */
export function UpdateBand() {
  const t = useT();
  const isReady = useSyncExternalStore(subscribe, () => ready, () => false);
  const { pathname, state } = useLocation();
  // a move to another page: what the last page held is gone already, so the new version loads here; never a move that
  // carries work with it (Claude's filled invoice or count, the order day's invoice cards), which a reload would drop
  useEffect(() => {
    if (state === null || state === undefined) reloadIfReady();
  }, [pathname]);
  if (!isReady) return null;
  return (
    <div className="update-band no-print" role="status" data-testid="update-band">
      {t('updateReady')}{' '}
      <button type="button" className="btn-link" onClick={() => { if (confirmLeaving()) reload(); }}>
        {t('updateReload')}
      </button>
    </div>
  );
}
